import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { loadPlayerJs } from '@/hooks/bunnyPlayerJs';

/** Fallback, falls das 'load'-Event des iframes bereits vor dem Effekt gefeuert hat (in ms) */
const ATTACH_FALLBACK_DELAY_MS = 4000;

/**
 * Liest die Sekunde aus einem player.js-'timeupdate'-Payload.
 * Bunny liefert das Payload je nach Player-Version als Objekt ODER als
 * JSON-String — beides wird hier ohne Casts behandelt. Liefert null,
 * wenn das Payload keine verwertbare Sekundenangabe enthält.
 */
function readSeconds(data?: unknown): number | null {
  let raw: unknown = data;
  if (typeof data === 'string') {
    try {
      raw = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (typeof raw !== 'object' || raw === null) return null;
  if (!('seconds' in raw) || typeof raw.seconds !== 'number') return null;
  return raw.seconds;
}

/**
 * Liefert true, sobald das Bunny-Video im übergebenen iframe über
 * `revealAfterSeconds` Sekunden hinaus abgespielt wurde (gemessen über das
 * player.js-Event 'timeupdate').
 *
 * Der Wert kippt genau einmal von false auf true und bleibt danach true —
 * Zurückspulen oder ein Neuladen des Players blendet den CTA also nicht
 * wieder aus.
 *
 * Kann player.js nicht geladen oder der Player nicht angebunden werden,
 * bleibt der Rückgabewert dauerhaft false (der CTA bleibt dann einfach
 * verborgen, es gibt keinen Fehler und keine Konsolenausgabe).
 *
 * @param iframeRef Ref auf das Bunny-Embed-iframe
 * @param revealAfterSeconds Abspielzeit in Sekunden, ab der true geliefert wird
 * @param enabled false deaktiviert den Hook komplett (z. B. wenn kein Video konfiguriert ist)
 */
export function useBunnyPlaybackReveal(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  revealAfterSeconds: number,
  enabled: boolean,
): boolean {
  const [revealed, setRevealed] = useState(false);
  // Verhindert wiederholtes setState bei jedem weiteren 'timeupdate'
  const revealedRef = useRef(false);

  useEffect(() => {
    if (enabled === false) return;

    const iframe = iframeRef.current;
    if (iframe === null) return;

    let cancelled = false;
    let player: PlayerJsPlayer | null = null;
    let fallbackTimeoutId: number | null = null;

    const handleTimeUpdate = (data?: unknown) => {
      const seconds = readSeconds(data);
      if (seconds === null) return;
      if (seconds < revealAfterSeconds) return;
      if (revealedRef.current === true) return;

      revealedRef.current = true;
      setRevealed(true);

      // Ab jetzt brauchen wir keine Updates mehr.
      if (player !== null) {
        try {
          player.off('timeupdate', handleTimeUpdate);
        } catch {
          // Player bereits zerstört — nichts zu tun.
        }
      }
    };

    const attach = async () => {
      // Idempotent: 'load'-Event und Timeout-Fallback können beide feuern.
      if (player !== null || cancelled) return;

      const ns = await loadPlayerJs();
      if (ns === null || cancelled || player !== null) return;

      try {
        player = new ns.Player(iframe);
        player.on('timeupdate', handleTimeUpdate);
      } catch {
        // player.js konnte sich nicht an das iframe binden — CTA bleibt verborgen.
        player = null;
      }
    };

    // Das 'load'-Event kann bereits gefeuert haben, bevor dieser Effekt lief
    // (z. B. bei StrictMode-Remount oder aus dem Cache), daher zusätzlich
    // ein Timeout-Fallback.
    iframe.addEventListener('load', attach);
    fallbackTimeoutId = window.setTimeout(attach, ATTACH_FALLBACK_DELAY_MS);

    return () => {
      cancelled = true;
      if (fallbackTimeoutId !== null) {
        window.clearTimeout(fallbackTimeoutId);
        fallbackTimeoutId = null;
      }
      iframe.removeEventListener('load', attach);
      if (player !== null) {
        try {
          player.off('timeupdate', handleTimeUpdate);
          player.destroy();
        } catch {
          // Player bereits zerstört — nichts zu tun.
        }
        player = null;
      }
    };
  }, [iframeRef, revealAfterSeconds, enabled]);

  return revealed;
}
